import type { ApiTokenCreatedData } from '../../model/api-tokens/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface ApiTokenCreateApiOptions {
  name: string
  expiresAt?: string | null
  onStart?: () => void
  onSuccess?: (data: ApiTokenCreatedData) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

export const useApiTokenCreateApi = async (
  options: ApiTokenCreateApiOptions,
): Promise<ApiTokenCreatedData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.api-tokens')

  options.onStart?.()

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, {
      name: options.name,
      ...(options.expiresAt ? { expires_at: options.expiresAt } : {}),
    })

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const createdData: ApiTokenCreatedData = data.data
    toast.success('Token created')
    options.onSuccess?.(createdData)
    return createdData
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    options.onFinish?.()
  }
}
